import { PropTypes, React } from 'imports';
import { FormattedResource } from 'typed/components/common';
import Tooltip from '../Tooltip/Tooltip';
import TabPanel from '../TabPanel/TabPanel';
import { deepEqual, toArray } from 'utils';
import './EntityEditForm.less';

export const propertyPropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
        get: PropTypes.func.isRequired,
        set: PropTypes.func.isRequired
    })
]);

export const invalidMessagePropType = PropTypes.oneOfType([
    PropTypes.bool,
    FormattedResource.resourcePropType,
    PropTypes.node
]);

function renderFields(children, entity, onFieldChange) {
    children = toArray(children || [])
        .flatten()
        .filter(child => child !== null);

    const
        COLUMN_GUTTER_WIDTH = 10,
        columns = children.filter(child => child.type === Column),
        columnWidth = columns.length > 0 ? `calc(${(100 / columns.length).toFixed(2)}% - ${COLUMN_GUTTER_WIDTH * (columns.length - 1) / columns.length}px)` : undefined;

    return children.map((child, index) => {
        if (child.type === Tab) {
            return <TabPanel.Tab key={index} {...child.props}>{renderFields(child.props.children, entity, onFieldChange)}</TabPanel.Tab>;
        }
        if (child.type === Group) {
            return <div key={index} className={`group ${child.props.className || ''}`} >
                <div className="label">
                    {React.isValidElement(child.props.name) ? child.props.name : <FormattedResource resource={child.props.name} />}
                </div>
                <div className="input">{renderFields(child.props.children, entity, onFieldChange)}</div>
            </div>;
        }
        if (child.type === Column) {
            const isLastColumn = child === columns[columns.length - 1];
            return <InternalColumn key={index} {...child.props} style={{ width: columnWidth, marginRight: isLastColumn ? '0' : `${COLUMN_GUTTER_WIDTH}px` }}>
                {renderFields(child.props.children, entity, onFieldChange)}
            </InternalColumn>;
        }
        else if (child.type === Field) {
            return <InternalField key={index} {...child.props} entity={entity} onChange={onFieldChange} />;
        }
        else {
            return child;
        }
    });
}


export class Tab extends React.Component {
    static get propTypes() {
        return {
            name: FormattedResource.resourcePropType
        };
    }
}

export class Group extends React.Component {
    static get propTypes() {
        return {
            className: PropTypes.string,
            name: PropTypes.oneOfType([FormattedResource.resourcePropType, PropTypes.node])
        };
    }
}

class InternalField extends React.Component {
    static get propTypes() {
        return {
            name: FormattedResource.resourcePropType,
            hint: FormattedResource.resourcePropType,
            entity: PropTypes.any.isRequired,
            property: propertyPropType.isRequired,
            mandatory: PropTypes.bool,
            readonly: PropTypes.bool,
            className: PropTypes.string,
            onChange: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            className: '',
            mandatory: false
        };
    }

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
        if (DEBUG) {
            if (!nextProps.entity || !nextProps.property ||
                (!nextProps.property.get || !nextProps.property.set) && !nextProps.entity.hasOwnProperty(nextProps.property)
            ) {
                throw new Error(`Entity ${nextProps.entity} has no property ${nextProps.property}.`);
            }

            const children = toArray(nextProps.children || []).flatten();
            let inputChild;
            if (!children || !children.length ||
                !(inputChild = children.find((child, _, __, actualType) => child?.type && (actualType = child.type.WrappedComponent || child.type) && actualType.propTypes && actualType.propTypes.value && (nextProps.readonly || actualType.propTypes.onChange)))) {
                throw new Error('EntityInputForm.Field should have one child with input interface - value/onChange props');
            }
            else if (inputChild.props && (
                inputChild.props.value !== undefined && !deepEqual(inputChild.props.value, (inputChild.type.WrappedComponent || inputChild.type).defaultProps.value) ||
                inputChild.props.onChange !== undefined && inputChild.props.onChange !== (inputChild.type.WrappedComponent || inputChild.type).defaultProps.onChange
            )) {
                throw new Error('EntityInputForm.Field\'s inner input component should not have assigned value/onChange props, all communication should go via EntityInputForm\'s props.');
            }
        }
    }

    _onChange = (sender, value, ...more) => {
        const newEntity = { ...this.props.entity };
        if (typeof this.props.property === 'string') {
            newEntity[this.props.property] = value;
        }
        else {
            this.props.property.set(newEntity, value, ...more);
        }
        this.props.onChange(this, newEntity, sender, ...more);
    };

    _getOnChangeHandler = (onChangeAdditionalAction) => {
        return (sender, value, ...more) => {
            this._onChange(sender, value, ...more);

            if (onChangeAdditionalAction) {
                onChangeAdditionalAction(sender, value);
            }
        };
    };

    render() {
        const
            children = toArray(this.props.children || []).flatten(),
            inputChild = children.find((child, _, __, actualType) => child?.type && (actualType = child.type.WrappedComponent || child.type) && actualType.propTypes && actualType.propTypes.value && (this.props.readonly || actualType.propTypes.onChange)),
            value = typeof this.props.property === 'string' ?
                this.props.entity[this.props.property] :
                this.props.property.get(this.props.entity);

        return (
            <div className={`field ${this.props.className}`}>
                <div className="label">
                    {this.props.name ? <FormattedResource resource={this.props.name} /> : ''}
                    {this.props.name && this.props.mandatory ? <sup>*</sup> : null}
                </div>
                <div className="input">
                    {children.map((child, index) =>
                        inputChild !== null && inputChild !== undefined && child === inputChild ?
                            <inputChild.type key={index} {...inputChild.props}
                                placeholder={inputChild.props.placeholder}
                                value={value === null || value === undefined ? null : value}
                                onChange={this._getOnChangeHandler(inputChild.props.onChangeAdditionalAction)}>{/* todo: find better way to execute additional action, than undocumented prop*/}
                                {inputChild.props.children}
                            </inputChild.type> :
                            child
                    )}
                </div>
                {this.props.hint ?
                    <div className="hint">
                        <FormattedResource resource={this.props.hint} />
                    </div> :
                    null
                }
            </div>
        );
    }
}

export class Field extends React.Component {
    static get propTypes() {
        return {
            name: FormattedResource.resourcePropType,
            hint: FormattedResource.resourcePropType,
            property: propertyPropType.isRequired,
            mandatory: PropTypes.bool,
            readonly: PropTypes.bool,
            className: PropTypes.string
        };
    }
}

class InternalColumn extends React.Component {

    static get propTypes() {
        return {
            className: PropTypes.string,
            style: PropTypes.objectOf(PropTypes.string).isRequired
        };
    }

    render() {
        return <div style={this.props.style} className={`column${this.props.className ? ` ${this.props.className}` : ''}`}>
            {this.props.children}
        </div>;
    }
}

export class Column extends React.Component {
    static get propTypes() {
        return {
            className: PropTypes.string
        };
    }
}

export default class EntityEditForm extends React.Component {
    static get propertyPropType() {
        return propertyPropType;
    }

    static get Tab() {
        return Tab;
    }

    static get Group() {
        return Group;
    }

    static get Field() {
        return Field;
    }

    static get Column() {
        return Column;
    }

    static get propTypes() {
        return {
            value: PropTypes.any,
            title: FormattedResource.resourcePropType,
            valid: PropTypes.bool,
            submit: FormattedResource.resourcePropType,
            cancel: FormattedResource.resourcePropType,
            invalidTooltip: invalidMessagePropType,
            onChange: PropTypes.func,
            onSubmit: PropTypes.func,
            onCancel: PropTypes.func,
            additionalButtons: PropTypes.arrayOf(PropTypes.oneOfType([
                PropTypes.node,
                PropTypes.shape({
                    name: FormattedResource.resourcePropType,
                    opposite: PropTypes.bool,
                    disabled: PropTypes.bool,
                    className: PropTypes.string,
                    onClick: PropTypes.func
                })
            ])),
            className: PropTypes.string
        };
    }

    static get defaultProps() {
        return {
            valid: true,
            submit: { id: 'common.save' },
            cancel: { id: 'common.cancel' },
            className: ''
        };
    }

    _onFieldChange = (sender, entity, originalSender, ...more) => {
        if (this.props.onChange) {
            this.props.onChange(this, entity, originalSender, ...more);
        }
    };

    _onSubmitClick = () => {
        if (this.props.onSubmit) {
            this.props.onSubmit(this, this.props.value);
        }
    };

    _onCancelClick = () => {
        if (this.props.onCancel) {
            this.props.onCancel(this);
        }
    };

    _onAdditionalButtonClick = (handler, ...args) => {
        if (handler) {
            handler(this, ...args);
        }
    };

    render() {
        return (
            <div className={`entity-edit-form ${this.props.className}`}>
                {
                    this.props.title ?
                        <div className="title">
                            <FormattedResource resource={this.props.title} />
                        </div> :
                        null
                }

                {
                    toArray(this.props.children).every(child => !child || child.type === Tab) ?
                        <TabPanel className="body">
                            {renderFields(this.props.children, this.props.value, this._onFieldChange)}
                        </TabPanel> :
                        <div className="body">
                            {renderFields(this.props.children, this.props.value, this._onFieldChange)}
                        </div>
                }

                {
                    this.props.additionalButtons && this.props.additionalButtons.length || this.props.submit || this.props.cancel ?
                        <div className="buttons flex-row">
                            {
                                this.props.additionalButtons ?
                                    this.props.additionalButtons
                                        .filter(v => v)
                                        .filter(({ opposite }) => opposite !== false)
                                        .map((item, index) =>
                                            <div key={index} className="button-col">
                                                {
                                                    React.isValidElement(item) ?
                                                        item :
                                                        ((({ name, onClick, ...props }) =>
                                                            <button {...props} onClick={this._onAdditionalButtonClick.bind(this, onClick)}>
                                                                {name ? <FormattedResource resource={name} /> : null}
                                                            </button>
                                                        ))(item)
                                                }
                                            </div>
                                        ) :
                                    null
                            }
                            <div className="main-col" />
                            {
                                this.props.additionalButtons ?
                                    this.props.additionalButtons
                                        .filter(v => v)
                                        .filter(({ opposite }) => opposite === false)
                                        .map(({ name, opposite, onClick, ...props }, index) =>
                                            <div key={index} className="button-col">
                                                <button {...props} onClick={this._onAdditionalButtonClick.bind(this, onClick)}>
                                                    {name ? <FormattedResource resource={name} /> : null}
                                                </button>
                                            </div>
                                        ) :
                                    null
                            }
                            {
                                this.props.submit ?
                                    <div className="button-col">
                                        <button className="primary" disabled={!this.props.valid} onClick={this._onSubmitClick}>
                                            <FormattedResource resource={this.props.submit} />
                                        </button>
                                        {
                                            !this.props.valid && this.props.invalidTooltip ?
                                                <div className="blocker">
                                                    <Tooltip><FormattedResource resource={this.props.invalidTooltip} /></Tooltip>
                                                </div> :
                                                null
                                        }
                                    </div> :
                                    null
                            }
                            {
                                this.props.cancel ?
                                    <div className="button-col">
                                        <button onClick={this._onCancelClick}>
                                            <FormattedResource resource={this.props.cancel} />
                                        </button>
                                    </div> :
                                    null
                            }
                        </div> :
                        null
                }
            </div>
        );
    }
}
